// Helpers for the TipTap rich-text "Learn More" field.
//
// long_description is stored as a JSON string in the DB (TipTap doc format).
// Legacy rows that predate the RTF upgrade contain plain text strings. This
// module handles both: parse, convert to HTML, and sanitize so the output is
// safe for dangerouslySetInnerHTML.
//
// All sanitization is done with DOMPurify. Only the tags needed for the
// editor's feature set are allowed — no scripts, no iframes, no event handlers.
import DOMPurify from 'dompurify';
import { generateHTML } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';

// The set of TipTap extensions must match what the editor uses exactly, so
// generateHTML produces valid HTML for every node type the editor can produce.
// StarterKit v3 bundles its own Link, so disable it (`link: false`) and add the
// explicitly-configured one to avoid a duplicate 'link' extension.
const EDITOR_EXTENSIONS = [
  StarterKit.configure({ link: false }),
  Link.configure({ openOnClick: false }),
];

// Tags and attributes the sanitizer permits. Covers everything StarterKit +
// Link can output; everything else is stripped.
const DOMPURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'em', 's',
    'h2', 'h3',
    'ul', 'ol', 'li',
    'blockquote',
    'a',
    'code', 'pre',
    'hr',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
};

// Try to parse a raw long_description value as a TipTap JSON document.
// Returns the parsed doc object on success, null for empty/null values,
// or a wrapped legacy plain-text paragraph doc if parsing fails.
export const parseTiptapValue = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw; // already parsed elsewhere

  try {
    const doc = JSON.parse(raw);
    // Minimal structural check — a TipTap doc always has type: 'doc'.
    if (doc && doc.type === 'doc') return doc;
  } catch {
    // Not valid JSON — treat as legacy plain text.
  }

  // Wrap legacy plain text in a single paragraph so the editor can display it.
  return {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: raw.trim() ? [{ type: 'text', text: raw }] : [],
      },
    ],
  };
};

// Convert a TipTap doc object to an HTML string using the shared extension set.
export const tiptapToHtml = (doc) => {
  if (!doc) return '';
  try {
    return generateHTML(doc, EDITOR_EXTENSIONS);
  } catch {
    return '';
  }
};

// Sanitize an HTML string to prevent XSS. Safe to use with dangerouslySetInnerHTML.
export const sanitizeHtml = (html) => {
  if (!html) return '';
  return DOMPurify.sanitize(html, DOMPURIFY_CONFIG);
};

// Full pipeline: raw DB value → sanitized HTML string for rendering.
// Returns '' for null/empty values.
export const renderableHtml = (raw) => {
  const doc = parseTiptapValue(raw);
  if (!doc) return '';
  return sanitizeHtml(tiptapToHtml(doc));
};

// Serialize a TipTap editor doc (the object returned by editor.getJSON()) to
// the string format stored in the DB.
export const tiptapToJsonString = (doc) => JSON.stringify(doc);

// Returns true if the stored value is non-empty (has meaningful text content).
export const hasContent = (raw) => {
  if (!raw) return false;
  const doc = parseTiptapValue(raw);
  if (!doc) return false;
  const html = tiptapToHtml(doc);
  // Strip all tags and check if any text remains.
  return html.replace(/<[^>]*>/g, '').trim().length > 0;
};

// Normalize a raw long_description for persistence. The server requires a valid
// TipTap JSON document (or empty), but drafts loaded from older rows can still
// hold legacy plain text. Convert such values to a proper JSON doc string, and
// collapse empty content to null. Safe to call on already-serialized JSON.
export const normalizeLongDescription = (raw) => {
  if (!hasContent(raw)) return null;
  return tiptapToJsonString(parseTiptapValue(raw));
};
